import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Tooltip,
    Checkbox,
    FormControlLabel,
    ListItemText,
    Paper,
    Stack,
    Divider,
    Radio,
    RadioGroup,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import useNotify from '../../hooks/useNotify';
import { useGetShows } from '@openapi/shows/shows';
import {
    useGetSalesReport,
    useGetTransactionsReport,
    useGetManifestReport,
    useGetCommissionReport,
    useGetAffiliateSalesReport,
    useGetAffiliateInvoiceReport,
    useGetPayoutReport,
    useGetBookingFeeReport,
    useGetAffiliateAgentReport,
    useGetAffiliateCancelledRefundedReport,
} from '@openapi/reports/reports';
import {
    useGetCommissionGroups,
    useGetVenueCommissionGroups,
} from '@openapi/commission-groups/commission-groups';
import { useGetPaymentMethods } from '@openapi/payment-methods/payment-methods';
import PageWithTitle from '../../components/atom/PageWithTitle';
import SalesReportTable from './components/SalesReportTable';
import TransactionsReportTable from './components/TransactionsReportTable';
import ManifestReportTable from './components/ManifestReportTable';
import CommissionReportTable from './components/CommissionReportTable';
import AffiliateSalesReportTable from './components/AffiliateSalesReportTable';
import AffiliateAgentReportTable from './components/AffiliateAgentReportTable';
import AffiliateInvoiceReportTable from './components/AffiliateInvoiceReportTable';
import PayoutReportTable from './components/PayoutReportTable';
import BookingFeeReportTable from './components/BookingFeeReportTable';
import AffiliateCancelledRefundedReportTable from './components/AffiliateCancelledRefundedReportTable';
import { exportSalesReportToXlsx } from './helpers/exportSalesReport';
import { exportTransactionsReportToXlsx } from './helpers/exportTransactionsReport';
import { exportManifestReportToXlsx } from './helpers/exportManifestReport';
import { exportCommissionReportToXlsx } from './helpers/exportCommissionReport';
import { exportAffiliateSalesReportToXlsx } from './helpers/exportAffiliateSalesReport';
import { exportAffiliateInvoiceReportToXlsx } from './helpers/exportAffiliateInvoiceReport';
import { exportAffiliateAgentReportToXlsx } from './helpers/exportAffiliateAgentReport';
import { exportPayoutReportToXlsx } from './helpers/exportPayoutReport';
import { exportBookingFeeReportToXlsx } from './helpers/exportBookingFeeReport';
import { exportAffiliateCancelledRefundedReportToXlsx } from './helpers/exportAffiliateCancelledRefundedReport';
import { useGetVenues } from '@openapi/venues/venues';
import { useGetUsers } from '@openapi/users/users';
import { CAPABILITIES } from '../../helpers/capabilities';
import { useCapabilities } from '../../hooks/useCapabilities';
import { useAuth } from '../../hooks/useAuth';
import { getDefaultDay, appendDateRangeParams } from '../../helpers/dateParams';

const REPORT_TYPES = {
    SALES: 'sales',
    TRANSACTIONS: 'transactions',
    MANIFEST: 'manifest',
    COMMISSION: 'commission',
    AFFILIATE_SALES: 'affiliateSales',
    AFFILIATE_AGENT: 'affiliateAgent',
    AFFILIATE_INVOICE: 'affiliateInvoice',
    PAYOUT: 'payout',
    BOOKING_FEE: 'bookingFee',
    AFFILIATE_CANCELLED_REFUNDED: 'affiliateCancelledRefunded',
};

const BOOKING_STATUSES = ['processing', 'finished', 'cancelled'];

// Integration source options for filtering
const INTEGRATION_SOURCES = [{ value: 'viator', label: 'reports.filters.viator' }];

// Commission group source options carry a `cg:` prefix to tell them apart from
// the fixed booking-origin buckets when building query params.
const COMMISSION_GROUP_PREFIX = 'cg:';

const REPORT_CAPABILITIES = {
    [REPORT_TYPES.SALES]: {
        view: CAPABILITIES.REPORTS_SALES_VIEW,
        export: CAPABILITIES.REPORTS_SALES_EXPORT,
        labelKey: 'reports.salesReport',
    },
    [REPORT_TYPES.TRANSACTIONS]: {
        view: CAPABILITIES.REPORTS_TRANSACTIONS_VIEW,
        export: CAPABILITIES.REPORTS_TRANSACTIONS_EXPORT,
        labelKey: 'reports.transactionsReport',
    },
    [REPORT_TYPES.MANIFEST]: {
        view: CAPABILITIES.REPORTS_MANIFEST_VIEW,
        export: CAPABILITIES.REPORTS_MANIFEST_EXPORT,
        labelKey: 'reports.manifestReport',
    },
    [REPORT_TYPES.COMMISSION]: {
        view: CAPABILITIES.REPORTS_COMMISSION_VIEW,
        export: CAPABILITIES.REPORTS_COMMISSION_EXPORT,
        labelKey: 'reports.commissionReport',
    },
    [REPORT_TYPES.AFFILIATE_SALES]: {
        view: CAPABILITIES.REPORTS_AFFILIATE_SALES_VIEW,
        export: CAPABILITIES.REPORTS_AFFILIATE_SALES_EXPORT,
        labelKey: 'reports.affiliateSalesReport',
    },
    [REPORT_TYPES.AFFILIATE_AGENT]: {
        view: CAPABILITIES.REPORTS_AFFILIATE_SALES_VIEW,
        export: CAPABILITIES.REPORTS_AFFILIATE_SALES_EXPORT,
        labelKey: 'reports.affiliateAgentReport',
    },
    [REPORT_TYPES.AFFILIATE_INVOICE]: {
        view: CAPABILITIES.REPORTS_AFFILIATE_INVOICE_VIEW,
        export: CAPABILITIES.REPORTS_AFFILIATE_INVOICE_EXPORT,
        labelKey: 'reports.affiliateInvoiceReport',
    },
    [REPORT_TYPES.PAYOUT]: {
        view: CAPABILITIES.REPORTS_PAYOUT_VIEW,
        export: CAPABILITIES.REPORTS_PAYOUT_EXPORT,
        labelKey: 'reports.payoutReport',
    },
    [REPORT_TYPES.BOOKING_FEE]: {
        view: CAPABILITIES.REPORTS_BOOKING_FEE_VIEW,
        export: CAPABILITIES.REPORTS_BOOKING_FEE_EXPORT,
        labelKey: 'reports.bookingFeeReport',
    },
    [REPORT_TYPES.AFFILIATE_CANCELLED_REFUNDED]: {
        view: CAPABILITIES.REPORTS_AFFILIATE_CANCELLED_REFUNDED_VIEW,
        export: CAPABILITIES.REPORTS_AFFILIATE_CANCELLED_REFUNDED_EXPORT,
        labelKey: 'reports.affiliateCancelledRefundedReport',
    },
};

const ReportsPage = () => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const { can } = useCapabilities();
    // Roles without venues.view (e.g. external_agent) get nothing from
    // useGetVenues — fall back to the venues stamped on the auth payload.
    const { user } = useAuth();

    const canViewReport = (type) => can(REPORT_CAPABILITIES[type].view);
    const canExportReport = (type) => can(REPORT_CAPABILITIES[type].export);

    // Filter states
    const [reportType, setReportType] = useState(REPORT_TYPES.SALES);
    const [venueId, setVenueId] = useState(null);
    const [showId, setShowId] = useState(0); // 0 = all shows
    const [showDateFrom, setShowDateFrom] = useState('');
    const [showDateTo, setShowDateTo] = useState('');
    const [bookingDateFrom, setBookingDateFrom] = useState('');
    const [bookingDateTo, setBookingDateTo] = useState('');
    const [bookingStatuses, setBookingStatuses] = useState(['finished']);
    const [taxBreakout, setTaxBreakout] = useState(false);
    const [breakdownNoAffiliate, setBreakdownNoAffiliate] = useState(false);
    const [breakdownNoAffiliateAgents, setBreakdownNoAffiliateAgents] = useState(false);
    const [hideNoAffiliate, setHideNoAffiliate] = useState(false);
    const [showTicketTypeBreakdown, setShowTicketTypeBreakdown] = useState(true);
    const [showTicketOptionBreakdown, setShowTicketOptionBreakdown] = useState(true);
    const [cancelledRefundedTicketTypeBreakdown, setCancelledRefundedTicketTypeBreakdown] =
        useState(false);
    const [cancelledRefundedTicketOptionBreakdown, setCancelledRefundedTicketOptionBreakdown] =
        useState(false);
    const [integrationSources, setIntegrationSources] = useState([]); // Empty = all, ['direct'] = direct only, ['viator'] = viator only
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [sources, setSources] = useState([]);
    const [bookingFeeSources, setBookingFeeSources] = useState([]);
    const [fetchedTypes, setFetchedTypes] = useState({});
    const markFetched = (type) => setFetchedTypes((s) => (s[type] ? s : { ...s, [type]: true }));
    const [manifestAllFields, setManifestAllFields] = useState(true);
    const [bookingDateMode, setBookingDateMode] = useState('created');
    const [showDay, setShowDay] = useState(getDefaultDay);
    const [bookingDay, setBookingDay] = useState('');

    const hasAnyDateFilter = [
        showDay,
        showDateFrom,
        showDateTo,
        bookingDay,
        bookingDateFrom,
        bookingDateTo,
    ].some(Boolean);

    const clearDateFilters = () => {
        setShowDay('');
        setShowDateFrom('');
        setShowDateTo('');
        setBookingDay('');
        setBookingDateFrom('');
        setBookingDateTo('');
    };
    const [agentId, setAgentId] = useState(0); // 0 = all agents
    const [commissionGroupId, setCommissionGroupId] = useState(0); // 0 = all groups
    const [agentCommissionGroupId, setAgentCommissionGroupId] = useState(0); // 0 = all groups
    const [hideNoGroup, setHideNoGroup] = useState(false);

    const effectiveShowFrom = showDay || showDateFrom;
    const effectiveShowTo = showDay || showDateTo;
    const effectiveBookingFrom = bookingDay || bookingDateFrom;
    const effectiveBookingTo = bookingDay || bookingDateTo;

    const pickShowDay = (value) => {
        setShowDay(value);
        if (value) {
            setShowDateFrom('');
            setShowDateTo('');
        }
    };
    const pickShowDateFrom = (value) => {
        setShowDateFrom(value);
        if (value) setShowDay('');
    };
    const pickShowDateTo = (value) => {
        setShowDateTo(value);
        if (value) setShowDay('');
    };
    const pickBookingDay = (value) => {
        setBookingDay(value);
        if (value) {
            setBookingDateFrom('');
            setBookingDateTo('');
        }
    };
    const pickBookingDateFrom = (value) => {
        setBookingDateFrom(value);
        if (value) setBookingDay('');
    };
    const pickBookingDateTo = (value) => {
        setBookingDateTo(value);
        if (value) setBookingDay('');
    };

    // Build query params — memoized so react-query keys stay referentially
    // stable across renders that don't change a filter.
    const queryParams = useMemo(() => {
        const params = {
            showId: showId || undefined,
            bookingStatuses: bookingStatuses.length > 0 ? bookingStatuses : undefined,
        };
        appendDateRangeParams(params, [
            ['showDateFrom', effectiveShowFrom],
            ['showDateTo', effectiveShowTo],
            ['bookingDateFrom', effectiveBookingFrom],
            ['bookingDateTo', effectiveBookingTo],
        ]);
        if (effectiveBookingFrom || effectiveBookingTo) {
            params.bookingDateMode = bookingDateMode;
        }
        if (
            taxBreakout &&
            (reportType === REPORT_TYPES.SALES || reportType === REPORT_TYPES.TRANSACTIONS)
        ) {
            params.taxBreakout = true;
        }

        if (reportType === REPORT_TYPES.AFFILIATE_SALES) {
            if (hideNoAffiliate) {
                params.hideNoAffiliate = true;
            } else {
                // Checkbox 2 (per-agent) implies checkbox 1 (split).
                if (breakdownNoAffiliate || breakdownNoAffiliateAgents) {
                    params.breakdownNoAffiliate = true;
                }
                if (breakdownNoAffiliateAgents) {
                    params.breakdownNoAffiliateAgents = true;
                }
            }
        }

        if (reportType === REPORT_TYPES.SALES && integrationSources.length > 0) {
            params.integrationSources = integrationSources;
        }

        if (reportType === REPORT_TYPES.TRANSACTIONS) {
            if (paymentTypes.length > 0) params.paymentTypes = paymentTypes;
            // Split the source selection into fixed buckets and commission groups
            const bucketSources = sources.filter((s) => !s.startsWith(COMMISSION_GROUP_PREFIX));
            const groupNames = sources
                .filter((s) => s.startsWith(COMMISSION_GROUP_PREFIX))
                .map((s) => s.slice(COMMISSION_GROUP_PREFIX.length));
            if (bucketSources.length > 0) params.sources = bucketSources;
            if (groupNames.length > 0) params.commissionGroups = groupNames;
        }

        if (reportType === REPORT_TYPES.BOOKING_FEE) {
            // Split the selection into fixed buckets and commission groups
            const bucketSources = bookingFeeSources.filter(
                (s) => !s.startsWith(COMMISSION_GROUP_PREFIX),
            );
            const groupNames = bookingFeeSources
                .filter((s) => s.startsWith(COMMISSION_GROUP_PREFIX))
                .map((s) => s.slice(COMMISSION_GROUP_PREFIX.length));
            if (bucketSources.length > 0) params.sources = bucketSources;
            if (groupNames.length > 0) params.commissionGroups = groupNames;
        }

        if (reportType === REPORT_TYPES.COMMISSION && agentId) {
            params.agentId = agentId;
        }

        if (reportType === REPORT_TYPES.AFFILIATE_INVOICE && commissionGroupId) {
            params.commissionGroupId = commissionGroupId;
        }

        if (reportType === REPORT_TYPES.AFFILIATE_AGENT && agentCommissionGroupId) {
            params.commissionGroupId = agentCommissionGroupId;
        }

        if (reportType === REPORT_TYPES.AFFILIATE_AGENT && hideNoGroup) {
            params.hideNoGroup = true;
        }

        return params;
    }, [
        showId,
        bookingStatuses,
        effectiveShowFrom,
        effectiveShowTo,
        effectiveBookingFrom,
        effectiveBookingTo,
        bookingDateMode,
        taxBreakout,
        breakdownNoAffiliate,
        breakdownNoAffiliateAgents,
        hideNoAffiliate,
        reportType,
        integrationSources,
        paymentTypes,
        sources,
        bookingFeeSources,
        agentId,
        commissionGroupId,
        agentCommissionGroupId,
        hideNoGroup,
    ]);

    // Fetch venues data
    const { data: venueData } = useGetVenues(
        {},
        {
            query: {
                onError: notifyError,
            },
        },
    );
    const venues = venueData?.venues || user?.venues || [];

    // Fetch shows for dropdown
    const { data: showsData } = useGetShows(
        { pageSize: 1000 },
        {
            query: {
                venueId: venueId || undefined,
                onError: notifyError,
            },
        },
    );

    // Payment methods for the selected venue — populates the payment-type filter
    const { data: paymentMethodsData } = useGetPaymentMethods(
        venueId,
        { pageSize: 1000 },
        {
            query: {
                enabled: !!venueId && reportType === REPORT_TYPES.TRANSACTIONS,
                onError: notifyError,
            },
        },
    );
    const paymentMethods = paymentMethodsData?.paymentMethods || [];

    const reportEnabled = (type) => !!venueId && !!fetchedTypes[type] && reportType === type;

    const {
        data: salesData,
        isLoading: loadingSales,
        refetch: refetchSales,
    } = useGetSalesReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.SALES), onError: notifyError },
    });

    const {
        data: transactionsData,
        isLoading: loadingTransactions,
        refetch: refetchTransactions,
    } = useGetTransactionsReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.TRANSACTIONS), onError: notifyError },
    });

    const {
        data: manifestData,
        isLoading: loadingManifest,
        refetch: refetchManifest,
    } = useGetManifestReport(venueId || user?.venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.MANIFEST), onError: notifyError },
    });

    // If the current selection isn't viewable (role with partial report access),
    // jump to the first one the user can see.
    useEffect(() => {
        if (canViewReport(reportType)) return;
        const firstAccessible = Object.values(REPORT_TYPES).find((type) => canViewReport(type));
        if (firstAccessible) setReportType(firstAccessible);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportType, can]);

    // Fetch users for agent dropdown (only if user can manage users)
    const canManageUsers = can(CAPABILITIES.USERS_MANAGE);
    const { data: usersData } = useGetUsers(
        venueId,
        { pageSize: 1000 },
        {
            query: {
                enabled: !!venueId && canManageUsers,
                onError: notifyError,
            },
        },
    );
    // Agents who only see their own bookings (no scope_all = external/affiliate agents)
    const agents = (usersData?.users || []).filter(
        (u) =>
            u.capabilities?.includes(CAPABILITIES.BOOKINGS_MANAGE) &&
            !u.capabilities?.includes(CAPABILITIES.BOOKINGS_SCOPE_ALL),
    );

    const { data: commissionGroupsData } = useGetCommissionGroups(Number(showId), {
        query: {
            enabled: !!showId && !!venueId && reportType === REPORT_TYPES.AFFILIATE_INVOICE,
            onError: notifyError,
        },
    });

    const commissionGroups = commissionGroupsData?.commissionGroups || [];

    const { data: venueCommissionGroupsData } = useGetVenueCommissionGroups(Number(venueId), {
        query: {
            enabled: !!venueId && reportType === REPORT_TYPES.AFFILIATE_AGENT,
            onError: notifyError,
        },
    });

    const venueCommissionGroups = venueCommissionGroupsData?.commissionGroups || [];

    const {
        data: commissionData,
        isLoading: loadingCommission,
        refetch: refetchCommission,
    } = useGetCommissionReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.COMMISSION), onError: notifyError },
    });

    const {
        data: affiliateSalesData,
        isLoading: loadingAffiliateSales,
        refetch: refetchAffiliateSales,
    } = useGetAffiliateSalesReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.AFFILIATE_SALES), onError: notifyError },
    });

    const {
        data: affiliateAgentData,
        isLoading: loadingAffiliateAgent,
        refetch: refetchAffiliateAgent,
    } = useGetAffiliateAgentReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.AFFILIATE_AGENT), onError: notifyError },
    });

    const {
        data: affiliateInvoiceData,
        isLoading: loadingAffiliateInvoice,
        refetch: refetchAffiliateInvoice,
    } = useGetAffiliateInvoiceReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.AFFILIATE_INVOICE), onError: notifyError },
    });

    const {
        data: payoutData,
        isLoading: loadingPayout,
        refetch: refetchPayout,
    } = useGetPayoutReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.PAYOUT), onError: notifyError },
    });

    const {
        data: bookingFeeData,
        isLoading: loadingBookingFee,
        refetch: refetchBookingFee,
    } = useGetBookingFeeReport(venueId, queryParams, {
        query: { enabled: reportEnabled(REPORT_TYPES.BOOKING_FEE), onError: notifyError },
    });

    const {
        data: affiliateCancelledRefundedData,
        isLoading: loadingAffiliateCancelledRefunded,
        refetch: refetchAffiliateCancelledRefunded,
    } = useGetAffiliateCancelledRefundedReport(
        venueId,
        { ...queryParams, bookingStatuses: undefined },
        {
            query: {
                enabled: reportEnabled(REPORT_TYPES.AFFILIATE_CANCELLED_REFUNDED),
                onError: notifyError,
            },
        },
    );

    // Trigger initial fetch for the active report type
    useEffect(() => {
        if (venueId) {
            markFetched(reportType);
        }
    }, [venueId, reportType]);

    // once vendors are loaded AND the filter is empty set it to the first value
    useEffect(() => {
        const availableVenues = venueData?.venues || user?.venues || [];
        if (availableVenues.length > 0 && !venueId) {
            setVenueId(availableVenues[0].id);
        }
    }, [venueData?.venues, user?.venues, venueId]);

    const dateFrom = effectiveShowFrom || effectiveBookingFrom;
    const dateTo = effectiveShowTo || effectiveBookingTo;

    const reportConfigs = {
        [REPORT_TYPES.SALES]: {
            data: salesData,
            isLoading: loadingSales,
            refetch: refetchSales,
            hasData: salesData?.tables?.length > 0,
            runExport: () =>
                exportSalesReportToXlsx(salesData, dateFrom, dateTo, taxBreakout, {
                    showIntegrationPrice:
                        integrationSources.length === 1 && integrationSources[0] !== 'direct',
                }),
        },
        [REPORT_TYPES.TRANSACTIONS]: {
            data: transactionsData,
            isLoading: loadingTransactions,
            refetch: refetchTransactions,
            hasData: transactionsData?.items?.length > 0,
            runExport: () =>
                exportTransactionsReportToXlsx(transactionsData, dateFrom, dateTo, taxBreakout, t),
        },
        [REPORT_TYPES.MANIFEST]: {
            data: manifestData,
            isLoading: loadingManifest,
            refetch: refetchManifest,
            hasData: manifestData?.items?.length > 0,
            runExport: () =>
                exportManifestReportToXlsx(manifestData, dateFrom, dateTo, manifestAllFields),
        },
        [REPORT_TYPES.COMMISSION]: {
            data: commissionData,
            isLoading: loadingCommission,
            refetch: refetchCommission,
            hasData: commissionData?.items?.length > 0,
            runExport: () => exportCommissionReportToXlsx(commissionData, dateFrom, dateTo),
        },
        [REPORT_TYPES.AFFILIATE_SALES]: {
            data: affiliateSalesData,
            isLoading: loadingAffiliateSales,
            refetch: refetchAffiliateSales,
            hasData: affiliateSalesData?.items?.length > 0,
            runExport: () =>
                exportAffiliateSalesReportToXlsx(affiliateSalesData, dateFrom, dateTo, {
                    showTicketType: showTicketTypeBreakdown,
                    showTicketOption: showTicketOptionBreakdown,
                }),
        },
        [REPORT_TYPES.AFFILIATE_AGENT]: {
            data: affiliateAgentData,
            isLoading: loadingAffiliateAgent,
            refetch: refetchAffiliateAgent,
            hasData: affiliateAgentData?.items?.length > 0,
            runExport: () => exportAffiliateAgentReportToXlsx(affiliateAgentData, dateFrom, dateTo),
        },
        [REPORT_TYPES.AFFILIATE_INVOICE]: {
            data: affiliateInvoiceData,
            isLoading: loadingAffiliateInvoice,
            refetch: refetchAffiliateInvoice,
            hasData: affiliateInvoiceData?.groups?.length > 0,
            runExport: () =>
                exportAffiliateInvoiceReportToXlsx(affiliateInvoiceData, dateFrom, dateTo),
        },
        [REPORT_TYPES.PAYOUT]: {
            data: payoutData,
            isLoading: loadingPayout,
            refetch: refetchPayout,
            hasData: !!payoutData?.summary,
            runExport: () => exportPayoutReportToXlsx(payoutData, dateFrom, dateTo),
        },
        [REPORT_TYPES.BOOKING_FEE]: {
            data: bookingFeeData,
            isLoading: loadingBookingFee,
            refetch: refetchBookingFee,
            hasData: bookingFeeData?.items?.length > 0,
            runExport: () => exportBookingFeeReportToXlsx(bookingFeeData, dateFrom, dateTo, t),
        },
        [REPORT_TYPES.AFFILIATE_CANCELLED_REFUNDED]: {
            data: affiliateCancelledRefundedData,
            isLoading: loadingAffiliateCancelledRefunded,
            refetch: refetchAffiliateCancelledRefunded,
            hasData: affiliateCancelledRefundedData?.items?.length > 0,
            runExport: () =>
                exportAffiliateCancelledRefundedReportToXlsx(
                    affiliateCancelledRefundedData,
                    dateFrom,
                    dateTo,
                    cancelledRefundedTicketTypeBreakdown,
                    cancelledRefundedTicketOptionBreakdown,
                ),
        },
    };

    const activeConfig = reportConfigs[reportType];

    const handleApplyFilters = () => {
        markFetched(reportType);
        activeConfig.refetch();
    };

    const handleExport = () => {
        if (!activeConfig.hasData || !activeConfig.runExport) return;
        activeConfig.runExport();
        notifySuccess(t('reports.exportSuccess'));
    };

    const handleStatusChange = (event) => {
        const value = event.target.value;
        setBookingStatuses(typeof value === 'string' ? value.split(',') : value);
    };

    const handleIntegrationChange = (event) => {
        const value = event.target.value;
        setIntegrationSources(typeof value === 'string' ? value.split(',') : value);
    };

    const getIntegrationLabel = (selected) => {
        if (!selected || selected.length === 0) return t('reports.filters.none');
        return selected
            .map((s) => {
                const source = INTEGRATION_SOURCES.find((src) => src.value === s);
                return source ? t(source.label) : s;
            })
            .join(', ');
    };

    // Source filter options: fixed buckets plus the venue's commission groups
    // (the group list is supplied by the transactions report response).
    const commissionGroupOptions = transactionsData?.commissionGroupOptions || [];
    const sourceOptions = [
        { value: 'widget', label: t('bookings.sourceOnline') },
        ...commissionGroupOptions.map((name) => ({
            value: `${COMMISSION_GROUP_PREFIX}${name}`,
            label: name,
        })),
        { value: 'admin', label: t('bookings.sourceCallCenter') },
        { value: 'viator', label: t('reports.filters.viator') },
        { value: 'fareharbor', label: t('reports.filters.fareharbor') },
    ];

    const handlePaymentTypesChange = (event) => {
        const value = event.target.value;
        setPaymentTypes(typeof value === 'string' ? value.split(',') : value);
    };

    const handleSourcesChange = (event) => {
        const value = event.target.value;
        setSources(typeof value === 'string' ? value.split(',') : value);
    };

    const getPaymentTypesLabel = (selected) => {
        if (!selected || selected.length === 0) return t('reports.filters.none');
        return selected
            .map((slug) => paymentMethods.find((pm) => pm.slug === slug)?.name || slug)
            .join(', ');
    };

    const getSourcesLabel = (selected) => {
        if (!selected || selected.length === 0) return t('reports.filters.none');
        return selected.map((s) => sourceOptions.find((o) => o.value === s)?.label || s).join(', ');
    };

    // Booking-fee source filter: fixed buckets plus the venue's commission groups
    // (group names come from the booking-fee report response), matching the
    // transactions report so affiliate bookings can be filtered by their group.
    const bookingFeeCommissionGroupOptions = bookingFeeData?.commissionGroupOptions || [];
    const bookingFeeSourceOptions = [
        { value: 'widget', label: t('bookings.sourceOnline') },
        ...bookingFeeCommissionGroupOptions.map((name) => ({
            value: `${COMMISSION_GROUP_PREFIX}${name}`,
            label: name,
        })),
        { value: 'admin', label: t('bookings.sourceCallCenter') },
        { value: 'viator', label: t('reports.filters.viator') },
        { value: 'fareharbor', label: t('reports.filters.fareharbor') },
    ];

    const handleBookingFeeSourcesChange = (event) => {
        const value = event.target.value;
        setBookingFeeSources(typeof value === 'string' ? value.split(',') : value);
    };

    const getBookingFeeSourcesLabel = (selected) => {
        if (!selected || selected.length === 0) return t('reports.filters.none');
        return selected
            .map((s) => bookingFeeSourceOptions.find((o) => o.value === s)?.label || s)
            .join(', ');
    };

    const isLoading = activeConfig.isLoading;
    const hasData = activeConfig.hasData;

    // Filter shows by selected venue
    const shows = showsData?.shows?.filter((show) => show.venueId === venueId) || [];

    // if the selected show is not in the filtered shows, reset to all shows
    useEffect(() => {
        if (showId === 0) {
            return;
        }
        const filteredShows = (showsData?.shows || []).filter((show) => show.venueId === venueId);
        if (!filteredShows.some((show) => show.id === showId)) {
            setShowId(0);
        }
    }, [showsData?.shows, venueId, showId]);

    useEffect(() => {
        setAgentId(0);
        setCommissionGroupId(0);
        setAgentCommissionGroupId(0);
        setHideNoGroup(false);
    }, [showId, reportType]);

    // Payment methods and commission groups are venue-specific — clear stale
    // selections on venue change.
    useEffect(() => {
        setPaymentTypes([]);
        setSources([]);
        setBookingFeeSources([]);
        setAgentCommissionGroupId(0);
    }, [venueId]);

    const getStatusLabel = (selected) => {
        if (!selected || selected.length === 0) return '';
        return selected.map((s) => t(`bookings.columns.${s}`, s)).join(', ');
    };

    return (
        <PageWithTitle title={t('reports.title')}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3,
                    width: '100%',
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                }}
            >
                {/* Sidebar Filters */}
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: '100%', md: 280 },
                        flexShrink: 0,
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        height: 'fit-content',
                        position: { xs: 'static', md: 'sticky' },
                        top: 80, // 64px header + 16px spacing
                        textAlign: 'left',
                    }}
                >
                    <Stack spacing={2.5} alignItems="stretch">
                        {/* Report Type */}
                        <FormControl fullWidth size="small">
                            <InputLabel
                                id="report-type-label"
                                sx={{
                                    '&.MuiInputLabel-shrink': {
                                        transform: 'scale(0.75) !important',
                                    },
                                }}
                            >
                                {t('reports.reportType')}
                            </InputLabel>
                            <Select
                                labelId="report-type-label"
                                value={reportType}
                                label={t('reports.reportType')}
                                onChange={(e) => setReportType(e.target.value)}
                            >
                                {Object.entries(REPORT_CAPABILITIES).map(
                                    ([type, cfg]) =>
                                        canViewReport(type) && (
                                            <MenuItem key={type} value={type}>
                                                {t(cfg.labelKey)}
                                            </MenuItem>
                                        ),
                                )}
                            </Select>
                        </FormControl>
                        {/* Venue filter */}
                        <FormControl fullWidth size="small">
                            <InputLabel
                                id="venue-select-label"
                                sx={{
                                    '&.MuiInputLabel-shrink': {
                                        transform: 'scale(0.75) !important',
                                    },
                                }}
                            >
                                {t('reports.filters.venue')}
                            </InputLabel>
                            <Select
                                labelId="venue-select-label"
                                value={venueId || ''}
                                label={t('reports.filters.venue')}
                                onChange={(e) => {
                                    // Venue is not changeable in current UI, but keeping for future use
                                    setVenueId(e.target.value);
                                }}
                            >
                                {venues.map((venue) => (
                                    <MenuItem key={venue.id} value={venue.id}>
                                        {venue.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Show filter */}
                        <FormControl fullWidth size="small">
                            <InputLabel
                                id="show-select-label"
                                sx={{
                                    '&.MuiInputLabel-shrink': {
                                        transform: 'scale(0.75) !important',
                                    },
                                }}
                            >
                                {t('reports.filters.show')}
                            </InputLabel>
                            <Select
                                labelId="show-select-label"
                                value={showId}
                                label={t('reports.filters.show')}
                                onChange={(e) => setShowId(e.target.value)}
                            >
                                <MenuItem value={0}>{t('reports.filters.allShows')}</MenuItem>
                                {shows.map((show) => (
                                    <MenuItem key={show.id} value={show.id}>
                                        {show.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Booking Status Multi-select - hidden for cancelled/refunded report (always shows cancelled+refunded) */}
                        {reportType !== REPORT_TYPES.AFFILIATE_CANCELLED_REFUNDED && (
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    id="status-select-label"
                                    sx={{
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'scale(0.75) !important',
                                        },
                                    }}
                                >
                                    {t('reports.filters.bookingStatus')}
                                </InputLabel>
                                <Select
                                    labelId="status-select-label"
                                    multiple
                                    value={bookingStatuses}
                                    onChange={handleStatusChange}
                                    label={t('reports.filters.bookingStatus')}
                                    renderValue={getStatusLabel}
                                >
                                    {BOOKING_STATUSES.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            <Checkbox
                                                checked={bookingStatuses.includes(status)}
                                                size="small"
                                            />
                                            <ListItemText
                                                primary={t(`bookings.columns.${status}`, status)}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Agent Filter - only for Commission Report, requires users.manage */}
                        {reportType === REPORT_TYPES.COMMISSION && canManageUsers && (
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    id="agent-select-label"
                                    sx={{
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'scale(0.75) !important',
                                        },
                                    }}
                                >
                                    {t('reports.filters.agent')}
                                </InputLabel>
                                <Select
                                    labelId="agent-select-label"
                                    value={agentId}
                                    label={t('reports.filters.agent')}
                                    onChange={(e) => setAgentId(e.target.value)}
                                >
                                    <MenuItem value={0}>{t('reports.filters.allAgents')}</MenuItem>
                                    {agents.map((agent) => (
                                        <MenuItem key={agent.id} value={agent.id}>
                                            {agent.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Commission Group Filter - only for Affiliate Invoice */}
                        {reportType === REPORT_TYPES.AFFILIATE_INVOICE && showId !== 0 && (
                            <FormControl fullWidth size="small">
                                <InputLabel id="commission-group-select-label">
                                    {t('reports.filters.commissionGroup')}
                                </InputLabel>
                                <Select
                                    labelId="commission-group-select-label"
                                    value={commissionGroupId}
                                    label={t('reports.filters.commissionGroup')}
                                    onChange={(e) => setCommissionGroupId(e.target.value)}
                                >
                                    <MenuItem value={0}>
                                        {t('reports.filters.allCommissionGroups')}
                                    </MenuItem>
                                    {commissionGroups.map((group) => (
                                        <MenuItem key={group.id} value={group.id}>
                                            {group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Commission Group Filter - only for Agent Performance */}
                        {reportType === REPORT_TYPES.AFFILIATE_AGENT && (
                            <FormControl fullWidth size="small">
                                <InputLabel id="agent-commission-group-select-label">
                                    {t('reports.filters.commissionGroup')}
                                </InputLabel>
                                <Select
                                    labelId="agent-commission-group-select-label"
                                    value={agentCommissionGroupId}
                                    label={t('reports.filters.commissionGroup')}
                                    onChange={(e) => setAgentCommissionGroupId(e.target.value)}
                                >
                                    <MenuItem value={0}>
                                        {t('reports.filters.allCommissionGroups')}
                                    </MenuItem>
                                    {venueCommissionGroups.map((group) => (
                                        <MenuItem key={group.id} value={group.id}>
                                            {group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {reportType === REPORT_TYPES.AFFILIATE_AGENT && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={hideNoGroup}
                                        onChange={(e) => setHideNoGroup(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('reports.filters.hideNoGroup')}
                                sx={{ ml: 0 }}
                            />
                        )}

                        {/* Integration Source Filter - only for Sales Report */}
                        {reportType === REPORT_TYPES.SALES && (
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    id="integration-select-label"
                                    sx={{
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'scale(0.75) !important',
                                        },
                                    }}
                                >
                                    {t('reports.filters.integrationSource')}
                                </InputLabel>
                                <Select
                                    labelId="integration-select-label"
                                    multiple
                                    value={integrationSources}
                                    onChange={handleIntegrationChange}
                                    label={t('reports.filters.integrationSource')}
                                    renderValue={getIntegrationLabel}
                                >
                                    {INTEGRATION_SOURCES.map((source) => (
                                        <MenuItem key={source.value} value={source.value}>
                                            <Checkbox
                                                checked={integrationSources.includes(source.value)}
                                                size="small"
                                            />
                                            <ListItemText primary={t(source.label)} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Payment Type Filter - only for Transactions Report */}
                        {reportType === REPORT_TYPES.TRANSACTIONS && (
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    id="payment-type-select-label"
                                    sx={{
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'scale(0.75) !important',
                                        },
                                    }}
                                >
                                    {t('reports.filters.paymentType')}
                                </InputLabel>
                                <Select
                                    labelId="payment-type-select-label"
                                    multiple
                                    value={paymentTypes}
                                    onChange={handlePaymentTypesChange}
                                    label={t('reports.filters.paymentType')}
                                    renderValue={getPaymentTypesLabel}
                                >
                                    {paymentMethods.map((pm) => (
                                        <MenuItem key={pm.slug} value={pm.slug}>
                                            <Checkbox
                                                checked={paymentTypes.includes(pm.slug)}
                                                size="small"
                                            />
                                            <ListItemText primary={pm.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Source Filter - only for Transactions Report */}
                        {reportType === REPORT_TYPES.TRANSACTIONS && (
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    id="source-select-label"
                                    sx={{
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'scale(0.75) !important',
                                        },
                                    }}
                                >
                                    {t('reports.filters.source')}
                                </InputLabel>
                                <Select
                                    labelId="source-select-label"
                                    multiple
                                    value={sources}
                                    onChange={handleSourcesChange}
                                    label={t('reports.filters.source')}
                                    renderValue={getSourcesLabel}
                                >
                                    {sourceOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Checkbox
                                                checked={sources.includes(opt.value)}
                                                size="small"
                                            />
                                            <ListItemText primary={opt.label} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {/* Source Filter - only for Booking Fee Report */}
                        {reportType === REPORT_TYPES.BOOKING_FEE && (
                            <FormControl fullWidth size="small">
                                <InputLabel
                                    id="booking-fee-source-select-label"
                                    sx={{
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'scale(0.75) !important',
                                        },
                                    }}
                                >
                                    {t('reports.filters.source')}
                                </InputLabel>
                                <Select
                                    labelId="booking-fee-source-select-label"
                                    multiple
                                    value={bookingFeeSources}
                                    onChange={handleBookingFeeSourcesChange}
                                    label={t('reports.filters.source')}
                                    renderValue={getBookingFeeSourcesLabel}
                                >
                                    {bookingFeeSourceOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            <Checkbox
                                                checked={bookingFeeSources.includes(opt.value)}
                                                size="small"
                                            />
                                            <ListItemText primary={opt.label} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <Divider />

                        {hasAnyDateFilter && (
                            <Box
                                sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}
                            >
                                <Button
                                    size="small"
                                    onClick={clearDateFilters}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {t('reports.filters.clearDates', 'Clear all date filters')}
                                </Button>
                            </Box>
                        )}

                        {/* Show Date Range */}
                        <Typography
                            variant="caption"
                            color="text.primary"
                            sx={{ mb: -1, textAlign: 'left', width: '100%', fontWeight: 600 }}
                        >
                            {t('reports.filters.showDateFrom').replace(' From', '')}
                        </Typography>
                        <TextField
                            type="date"
                            label={t('reports.filters.day')}
                            value={showDay}
                            onChange={(e) => pickShowDay(e.target.value)}
                            size="small"
                            fullWidth
                            disabled={!!showDateFrom || !!showDateTo}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                type="date"
                                label={t('reports.filters.dateFrom')}
                                value={showDateFrom}
                                onChange={(e) => pickShowDateFrom(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={!!showDay}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                type="date"
                                label={t('reports.filters.dateTo')}
                                value={showDateTo}
                                onChange={(e) => pickShowDateTo(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={!!showDay}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Box>

                        {/* Booking Date Range */}
                        <Typography
                            variant="caption"
                            color="text.primary"
                            sx={{ mb: -1, textAlign: 'left', width: '100%', fontWeight: 600 }}
                        >
                            {t('reports.filters.bookingDateFrom').replace(' From', '')}
                        </Typography>
                        <TextField
                            type="date"
                            label={t('reports.filters.day')}
                            value={bookingDay}
                            onChange={(e) => pickBookingDay(e.target.value)}
                            size="small"
                            fullWidth
                            disabled={!!bookingDateFrom || !!bookingDateTo}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                type="date"
                                label={t('reports.filters.dateFrom')}
                                value={bookingDateFrom}
                                onChange={(e) => pickBookingDateFrom(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={!!bookingDay}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                            <TextField
                                type="date"
                                label={t('reports.filters.dateTo')}
                                value={bookingDateTo}
                                onChange={(e) => pickBookingDateTo(e.target.value)}
                                size="small"
                                fullWidth
                                disabled={!!bookingDay}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Box>
                        {(bookingDateFrom || bookingDateTo || bookingDay) && (
                            <RadioGroup
                                row
                                value={bookingDateMode}
                                onChange={(e) => setBookingDateMode(e.target.value)}
                                sx={{ mt: -0.5 }}
                            >
                                <FormControlLabel
                                    value="created"
                                    control={<Radio size="small" />}
                                    label={t(
                                        'reports.filters.bookingDateCreated',
                                        'Booking Created',
                                    )}
                                    slotProps={{ typography: { variant: 'caption' } }}
                                />
                                <FormControlLabel
                                    value="lastModified"
                                    control={<Radio size="small" />}
                                    label={t(
                                        'reports.filters.bookingDateLastModified',
                                        'Last Modified',
                                    )}
                                    slotProps={{ typography: { variant: 'caption' } }}
                                />
                            </RadioGroup>
                        )}

                        {/* Tax Breakout Checkbox - only for Sales and Transactions
                            (the only reports that send/use taxBreakout) */}
                        {(reportType === REPORT_TYPES.SALES ||
                            reportType === REPORT_TYPES.TRANSACTIONS) && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={taxBreakout}
                                        onChange={(e) => setTaxBreakout(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('reports.filters.taxBreakout')}
                                sx={{ ml: 0 }}
                            />
                        )}

                        {/* No-affiliate controls - only for Affiliate Sales. Hide
                            takes precedence; breakdown checkbox 2 (per-agent)
                            implies checkbox 1 (split). */}
                        {reportType === REPORT_TYPES.AFFILIATE_SALES && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={showTicketTypeBreakdown}
                                            onChange={(e) =>
                                                setShowTicketTypeBreakdown(e.target.checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label={t(
                                        'reports.filters.showTicketTypeBreakdown',
                                        'Show ticket type breakdown',
                                    )}
                                    sx={{ ml: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={showTicketOptionBreakdown}
                                            onChange={(e) =>
                                                setShowTicketOptionBreakdown(e.target.checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label={t(
                                        'reports.filters.showTicketOptionBreakdown',
                                        'Show ticket option breakdown',
                                    )}
                                    sx={{ ml: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={hideNoAffiliate}
                                            onChange={(e) => setHideNoAffiliate(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={t('reports.filters.hideNoAffiliate')}
                                    sx={{ ml: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                breakdownNoAffiliate || breakdownNoAffiliateAgents
                                            }
                                            disabled={hideNoAffiliate}
                                            onChange={(e) => {
                                                setBreakdownNoAffiliate(e.target.checked);
                                                if (!e.target.checked) {
                                                    setBreakdownNoAffiliateAgents(false);
                                                }
                                            }}
                                            size="small"
                                        />
                                    }
                                    label={t('reports.filters.breakdownNoAffiliate')}
                                    sx={{ ml: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={breakdownNoAffiliateAgents}
                                            disabled={hideNoAffiliate}
                                            onChange={(e) =>
                                                setBreakdownNoAffiliateAgents(e.target.checked)
                                            }
                                            size="small"
                                        />
                                    }
                                    label={t('reports.filters.breakdownNoAffiliateAgents')}
                                    sx={{ ml: 0 }}
                                />
                            </>
                        )}

                        {/* Ticket breakdown controls - only for Affiliate Cancelled/Refunded */}
                        {reportType === REPORT_TYPES.AFFILIATE_CANCELLED_REFUNDED && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={cancelledRefundedTicketTypeBreakdown}
                                            onChange={(e) =>
                                                setCancelledRefundedTicketTypeBreakdown(
                                                    e.target.checked,
                                                )
                                            }
                                            size="small"
                                        />
                                    }
                                    label={t(
                                        'reports.filters.showTicketTypeBreakdown',
                                        'Show ticket type breakdown',
                                    )}
                                    sx={{ ml: 0 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={cancelledRefundedTicketOptionBreakdown}
                                            onChange={(e) =>
                                                setCancelledRefundedTicketOptionBreakdown(
                                                    e.target.checked,
                                                )
                                            }
                                            size="small"
                                        />
                                    }
                                    label={t(
                                        'reports.filters.showTicketOptionBreakdown',
                                        'Show ticket option breakdown',
                                    )}
                                    sx={{ ml: 0 }}
                                />
                            </>
                        )}

                        {/* Include All Fields Checkbox - only for Manifest */}
                        {reportType === REPORT_TYPES.MANIFEST && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={manifestAllFields}
                                        onChange={(e) => setManifestAllFields(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={t('reports.filters.includeAllFields')}
                                sx={{ ml: 0 }}
                            />
                        )}

                        <Divider />

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                fullWidth
                                size="small"
                            >
                                {t('common.apply')}
                            </Button>
                            {canExportReport(reportType) && activeConfig.runExport && (
                                <Tooltip title={t('reports.export')}>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            onClick={handleExport}
                                            disabled={!hasData}
                                            size="small"
                                        >
                                            <FileDownloadIcon />
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                        </Box>
                    </Stack>
                </Paper>

                {/* Main Content Area */}
                <Box
                    sx={{
                        flexGrow: 1,
                        // minWidth:0 lets this flex child shrink to the row width instead of
                        // being stretched by wide tables; each report table owns its own
                        // TableContainer (overflow-x + maxHeight + stickyHeader), so a wide
                        // table scrolls inside its container and the column header pins there
                        // rather than the whole content area scrolling the page sideways.
                        minWidth: 0,
                        width: '100%',
                    }}
                >
                    {/* Loading State */}
                    {isLoading && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={200}
                        >
                            <CircularProgress />
                        </Box>
                    )}

                    {/* No Data State */}
                    {!isLoading && !hasData && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight={200}
                            sx={{ width: '100%' }}
                        >
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ textAlign: 'center' }}
                            >
                                {t('reports.noData')}
                            </Typography>
                        </Box>
                    )}

                    {/* Sales Report Tables */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.SALES &&
                        salesData?.tables?.map((table) => (
                            <SalesReportTable
                                key={`${table.showId}-${table.ticketTypeId}`}
                                table={table}
                                taxBreakout={taxBreakout}
                                showIntegrationPrice={
                                    integrationSources.length === 1 &&
                                    integrationSources[0] !== 'direct'
                                }
                                showUnrealisedBalance={salesData?.tables?.some(
                                    (t) =>
                                        t.summary?.totalUnrealisedBalance ||
                                        t.items?.some(
                                            (i) => i.unrealisedBalance || i.unrealisedBalanceRef,
                                        ),
                                )}
                            />
                        ))}

                    {/* Transactions Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.TRANSACTIONS &&
                        transactionsData?.items?.length > 0 && (
                            <TransactionsReportTable
                                items={transactionsData.items}
                                reportData={transactionsData}
                                showId={showId}
                                taxBreakout={taxBreakout}
                            />
                        )}

                    {/* Manifest Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.MANIFEST &&
                        manifestData?.items?.length > 0 && (
                            <ManifestReportTable
                                items={manifestData.items}
                                groups={manifestData.groups}
                                showId={showId}
                            />
                        )}

                    {/* Commission Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.COMMISSION &&
                        commissionData?.items?.length > 0 && (
                            <CommissionReportTable
                                items={commissionData.items}
                                showId={showId}
                                totalLineAmount={commissionData.totalLineAmount}
                                totalCommission={commissionData.totalCommission}
                            />
                        )}

                    {/* Affiliate Sales Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.AFFILIATE_SALES &&
                        affiliateSalesData?.items?.length > 0 && (
                            <AffiliateSalesReportTable
                                items={affiliateSalesData.items}
                                totalSales={affiliateSalesData.totalSales}
                                totalBookings={affiliateSalesData.totalBookings}
                                totalPax={affiliateSalesData.totalPax}
                                showTicketType={showTicketTypeBreakdown}
                                showTicketOption={showTicketOptionBreakdown}
                            />
                        )}

                    {/* Agent Performance Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.AFFILIATE_AGENT &&
                        affiliateAgentData?.items?.length > 0 && (
                            <AffiliateAgentReportTable
                                items={affiliateAgentData.items}
                                totalSales={affiliateAgentData.totalSales}
                                totalBookings={affiliateAgentData.totalBookings}
                            />
                        )}

                    {/* Affiliate Invoice Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.AFFILIATE_INVOICE &&
                        affiliateInvoiceData?.groups?.length > 0 && (
                            <AffiliateInvoiceReportTable
                                groups={affiliateInvoiceData.groups}
                                showId={showId}
                                totalGrossAmount={affiliateInvoiceData.totalGrossAmount}
                                totalCommissionAmount={affiliateInvoiceData.totalCommissionAmount}
                                totalInvoiceAmount={affiliateInvoiceData.totalInvoiceAmount}
                            />
                        )}

                    {/* Payout Report */}
                    {!isLoading && reportType === REPORT_TYPES.PAYOUT && payoutData?.summary && (
                        <PayoutReportTable
                            summary={payoutData.summary}
                            excluded={payoutData.excluded}
                            transactions={payoutData.transactions}
                        />
                    )}
                    {/* Booking Fee Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.BOOKING_FEE &&
                        bookingFeeData?.items?.length > 0 && (
                            <BookingFeeReportTable
                                items={bookingFeeData.items}
                                summary={bookingFeeData.summary}
                            />
                        )}

                    {/* Affiliate Cancelled/Refunded Report Table */}
                    {!isLoading &&
                        reportType === REPORT_TYPES.AFFILIATE_CANCELLED_REFUNDED &&
                        affiliateCancelledRefundedData?.items?.length > 0 && (
                            <AffiliateCancelledRefundedReportTable
                                items={affiliateCancelledRefundedData.items}
                                totalBookings={affiliateCancelledRefundedData.totalBookings}
                                totalOriginalAmount={
                                    affiliateCancelledRefundedData.totalOriginalAmount
                                }
                                totalRefundedAmount={
                                    affiliateCancelledRefundedData.totalRefundedAmount
                                }
                                totalProcessingFees={
                                    affiliateCancelledRefundedData.totalProcessingFees
                                }
                                showTicketTypeBreakdown={cancelledRefundedTicketTypeBreakdown}
                                showTicketOptionBreakdown={cancelledRefundedTicketOptionBreakdown}
                            />
                        )}
                </Box>
            </Box>
        </PageWithTitle>
    );
};

export default ReportsPage;
